import * as THREE from 'three';
import type { Country } from '../data';
import { raycastToLatLon } from './raycast';
import { CountryHitTester } from './point-in-country';
import type { MarkerManager } from '../markers';

/** Duck-typed check: does this marker's `data` payload carry a country? */
function markerDataAsCountry(data: unknown): Country | null {
  if (
    typeof data === 'object' &&
    data !== null &&
    'iso_a2' in data &&
    typeof (data as { iso_a2: unknown }).iso_a2 === 'string'
  ) {
    return data as Country;
  }
  return null;
}

/**
 * Owns all interaction-related pointer listeners on the canvas and dispatches
 * hover / click events to the engine's caller.
 *
 * Hover: pointermove updates cached (x, y); per-frame `tick()` from the
 * render loop runs the raycast + hit-test + emits only when the hovered
 * country changes.
 *
 * Click: CUSTOM detection (not the browser's native `click` event). A click
 * only fires if all of the following are true for the pointer sequence:
 *   - Only one pointer was active (no pinch attempt)
 *   - TOTAL movement from pointerdown position stayed ≤ 6 px throughout
 *     (not just start-to-end — any mid-drag excursion cancels)
 *   - Duration ≤ 500 ms (long presses reserved for a future gesture)
 * This avoids the common "I panned the globe and released near where I
 * started — that shouldn't be a click" failure mode.
 *
 * Mobile: hover is silently skipped on touch-only devices via the
 * `(hover: hover)` media query. Click still works (taps have near-zero
 * movement, well within the threshold).
 */
/**
 * Payload emitted by `onMapClick` for every globe click — both the lat/lon
 * (raw coordinate the user tapped) and the resolved country (null for
 * ocean / unassigned region). City-distance modes consume the lat/lon;
 * country-tap modes consume the country; modes that need both get both.
 */
export interface MapClickEvent {
  lat: number;
  lon: number;
  country: Country | null;
}

export interface InteractionDispatcherOptions {
  element: HTMLElement;
  camera: THREE.PerspectiveCamera;
  globeGroup: THREE.Group;
  /**
   * Optional at construction — pointer-event handling and lat/lon
   * resolution work without it. Country resolution returns null until
   * `setHitTester` attaches one (typically when polygons load). Lets
   * city-distance modes get raw click events without paying the polygon
   * fetch they don't need.
   */
  hitTester?: CountryHitTester | null;
  markerManager: MarkerManager;
  onHover: (country: Country | null) => void;
  /**
   * Country-only click handler — null for ocean. Same callback shape as
   * before; kept for back-compat. New code can prefer `onMapClick` to
   * also receive raw lat/lon.
   */
  onClick: (country: Country | null) => void;
  /**
   * Per-click handler with lat/lon + resolved country. Fires for every
   * globe click (including ocean — country will be null).
   */
  onMapClick?: (event: MapClickEvent) => void;
}

const CLICK_MOVEMENT_THRESHOLD_PX = 6;
const CLICK_MAX_DURATION_MS = 500;

export class InteractionDispatcher {
  private readonly element: HTMLElement;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly globeGroup: THREE.Group;
  // Mutable so loadBorders can attach a real hit-tester after polygons land.
  // Null means "no country resolution available yet" — clicks still fire
  // onMapClick with country = null.
  private hitTester: CountryHitTester | null;
  private readonly markerManager: MarkerManager;
  private readonly onHover: (country: Country | null) => void;
  private readonly onClick: (country: Country | null) => void;
  private readonly onMapClick?: (event: MapClickEvent) => void;
  private readonly deviceHasHover: boolean;

  // Hover state
  private mouseX = 0;
  private mouseY = 0;
  private mouseInBounds = false;
  private hoverEnabled = true;
  private clickEnabled = true;
  private lastHoveredIso: string | null = null;

  // Click detection state
  private clickDownX = 0;
  private clickDownY = 0;
  private clickDownTime = 0;
  private clickPointerId: number | null = null;
  private clickCancelled = false;
  private activePointerCount = 0;

  constructor(options: InteractionDispatcherOptions) {
    this.element = options.element;
    this.camera = options.camera;
    this.globeGroup = options.globeGroup;
    this.hitTester = options.hitTester ?? null;
    this.markerManager = options.markerManager;
    this.onHover = options.onHover;
    this.onClick = options.onClick;
    this.onMapClick = options.onMapClick;
    this.deviceHasHover =
      typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

    this.element.addEventListener('pointerdown', this.handlePointerDown);
    this.element.addEventListener('pointermove', this.handlePointerMove);
    this.element.addEventListener('pointerup', this.handlePointerUp);
    this.element.addEventListener('pointercancel', this.handlePointerCancel);
    this.element.addEventListener('pointerenter', this.handlePointerEnter);
    this.element.addEventListener('pointerleave', this.handlePointerLeave);
  }

  setHoverEnabled(enabled: boolean): void {
    this.hoverEnabled = enabled;
    if (!enabled && this.lastHoveredIso !== null) {
      this.lastHoveredIso = null;
      this.markerManager.setHovered(null);
      this.onHover(null);
    }
  }

  setClickEnabled(enabled: boolean): void {
    this.clickEnabled = enabled;
  }

  /**
   * Attach (or detach with `null`) the country hit-tester. Pointer events
   * keep flowing either way; this just enables / disables the country
   * resolution that lands in `country` on click and `lastHoveredIso` on
   * hover. Called by `MapEngine.loadBorders` once polygons land.
   */
  setHitTester(hitTester: CountryHitTester | null): void {
    this.hitTester = hitTester;
  }

  /** Whether country resolution is currently available. */
  hasHitTester(): boolean {
    return this.hitTester !== null;
  }

  /** Called once per render frame from the engine's render loop. */
  tick(): void {
    if (!this.hoverEnabled || !this.deviceHasHover || !this.mouseInBounds) {
      if (this.lastHoveredIso !== null) {
        this.lastHoveredIso = null;
        this.markerManager.setHovered(null);
        this.onHover(null);
      }
      return;
    }

    const rect = this.element.getBoundingClientRect();
    const localX = this.mouseX - rect.left;
    const localY = this.mouseY - rect.top;

    // Priority 1 — marker hit. Fixes the "small-country dot resolves to
    // surrounding country" issue for tiny states (Vatican inside Italy etc).
    const markerHit = this.markerManager.findNearestAt(
      localX,
      localY,
      this.camera,
      rect.width,
      rect.height,
    );
    const markerCountry = markerHit ? markerDataAsCountry(markerHit.data) : null;

    // Tell MarkerManager which dot (if any) is hovered so it can swap style
    // and raise renderOrder — gives overlapping dots a clear winner.
    this.markerManager.setHovered(markerHit?.id ?? null);

    // Priority 2 — country PIP against the globe surface.
    const country =
      markerCountry ??
      (() => {
        const latlon = raycastToLatLon(
          this.camera,
          this.globeGroup,
          localX,
          localY,
          rect.width,
          rect.height,
        );
        return latlon && this.hitTester ? this.hitTester.find(latlon.lat, latlon.lon) : null;
      })();

    const iso = country?.iso_a2 ?? null;
    if (iso !== this.lastHoveredIso) {
      this.lastHoveredIso = iso;
      this.onHover(country);
    }
  }

  dispose(): void {
    this.element.removeEventListener('pointerdown', this.handlePointerDown);
    this.element.removeEventListener('pointermove', this.handlePointerMove);
    this.element.removeEventListener('pointerup', this.handlePointerUp);
    this.element.removeEventListener('pointercancel', this.handlePointerCancel);
    this.element.removeEventListener('pointerenter', this.handlePointerEnter);
    this.element.removeEventListener('pointerleave', this.handlePointerLeave);
  }

  // -- Pointer event handlers --

  private handlePointerDown = (e: PointerEvent): void => {
    this.activePointerCount++;

    // Second (or later) pointer joining invalidates any in-progress click —
    // user is starting a pinch or dragging with multi-touch.
    if (this.activePointerCount > 1) {
      this.clickCancelled = true;
      return;
    }

    this.clickDownX = e.clientX;
    this.clickDownY = e.clientY;
    this.clickDownTime = performance.now();
    this.clickPointerId = e.pointerId;
    this.clickCancelled = false;
  };

  private handlePointerMove = (e: PointerEvent): void => {
    // Hover state (cached for the next tick).
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;

    // Click cancellation — if the tracked pointer ever strays > threshold
    // from its down position, the gesture is a drag, not a click.
    if (this.clickCancelled || this.clickPointerId !== e.pointerId) return;
    const dx = e.clientX - this.clickDownX;
    const dy = e.clientY - this.clickDownY;
    if (dx * dx + dy * dy > CLICK_MOVEMENT_THRESHOLD_PX * CLICK_MOVEMENT_THRESHOLD_PX) {
      this.clickCancelled = true;
    }
  };

  private handlePointerUp = (e: PointerEvent): void => {
    this.activePointerCount = Math.max(0, this.activePointerCount - 1);

    // Only consider the pointer we were tracking.
    if (this.clickPointerId !== e.pointerId) return;

    const cancelled = this.clickCancelled;
    const dt = performance.now() - this.clickDownTime;
    this.clickPointerId = null;
    this.clickCancelled = false;

    if (cancelled || dt > CLICK_MAX_DURATION_MS) return;
    if (!this.clickEnabled) return;

    const rect = this.element.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;

    // Priority 1 — marker hit. Clicking near a small-country dot resolves
    // to that country directly, bypassing the PIP-precision issue at
    // normal zoom levels for sub-degree countries.
    const markerHit = this.markerManager.findNearestAt(
      localX,
      localY,
      this.camera,
      rect.width,
      rect.height,
    );
    const markerCountry = markerHit ? markerDataAsCountry(markerHit.data) : null;
    if (markerCountry) {
      this.onClick(markerCountry);
      // Marker hits don't have a meaningful raw lat/lon (the user clicked
      // a dot, not the surface) — but we still emit the marker's position
      // so distance modes can use a marker click as the user's guess.
      const pos = markerHit?.position;
      if (pos) this.onMapClick?.({ lat: pos.lat, lon: pos.lon, country: markerCountry });
      return;
    }

    // Priority 2 — globe surface PIP.
    const latlon = raycastToLatLon(
      this.camera,
      this.globeGroup,
      localX,
      localY,
      rect.width,
      rect.height,
    );
    const country = latlon && this.hitTester ? this.hitTester.find(latlon.lat, latlon.lon) : null;
    this.onClick(country);
    if (latlon) this.onMapClick?.({ lat: latlon.lat, lon: latlon.lon, country });
  };

  private handlePointerCancel = (e: PointerEvent): void => {
    this.activePointerCount = Math.max(0, this.activePointerCount - 1);
    if (this.clickPointerId === e.pointerId) {
      this.clickPointerId = null;
      this.clickCancelled = false;
    }
  };

  private handlePointerEnter = (): void => {
    this.mouseInBounds = true;
  };

  private handlePointerLeave = (): void => {
    this.mouseInBounds = false;
  };
}
